import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

// Componentes públicos
import DashboardInicio from './pages/public/dashboarinicio';
import Servicios from './pages/public/servicios';
import Nosotros from './pages/public/nosotros';
import Contactos from './pages/public/contactos';
import PetshopPublico from './pages/public/petshop';

// Componentes privados/admin
import DashboardAdmin from './pages/admin/dashboardmin';
import Login from './pages/auth/Login';
import AgendaPrivada from './pages/admin/AgendaPrivada';
import GestionPacientes from './pages/admin/GestionPacientes';
import Inventario from './pages/admin/Inventario';
import Petshop from './pages/admin/Petshop';
import Finanzas from './pages/admin/Finanzas';
import CertificadosViaje from './pages/admin/CertificadosViaje';

// Context de autenticación y componente de protección
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';

const SITE_URL = 'https://belkysveterinaria.com';

const PUBLIC_SEO = {
  '/': {
    title: 'Belkys Veterinaria | Servicios Veterinarios a Domicilio en Popayan',
    description:
      'Servicios veterinarios profesionales a domicilio en Popayan, Cauca. Consultas, vacunacion, desparasitacion y tratamientos para tu mascota.',
  },
  '/servicios': {
    title: 'Servicios Veterinarios a Domicilio | Belkys Veterinaria',
    description:
      'Consulta veterinaria, vacunacion, desparasitacion y atencion preventiva a domicilio en Popayan.',
  },
  '/nosotros': {
    title: 'Nosotros | Belkys Veterinaria',
    description:
      'Conoce a Belkys Veterinaria y nuestro enfoque de atencion profesional y cercana para mascotas en Popayan.',
  },
  '/petshop': {
    title: 'Petshop | Belkys Veterinaria',
    description:
      'Productos y accesorios para tu mascota en el Petshop de Belkys Veterinaria, Popayan.',
  },
  '/contactos': {
    title: 'Contacto | Belkys Veterinaria',
    description:
      'Agenda tu cita veterinaria a domicilio en Popayan. Contacto directo por WhatsApp y telefono.',
  },
};

function updateMetaTag(name, content) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updatePropertyMetaTag(property, content) {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function updateCanonicalTag(href) {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', href);
}

function normalizePathname(pathname) {
  if (pathname !== '/' && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname;
}

function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const normalizedPath = normalizePathname(location.pathname);
    const seo = PUBLIC_SEO[normalizedPath];
    const canonicalPath = normalizedPath === '/' ? '' : normalizedPath;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;

    if (seo) {
      document.title = seo.title;
      updateMetaTag('description', seo.description);
      updateMetaTag('robots', 'index, follow');
      updateCanonicalTag(canonicalUrl);
      updatePropertyMetaTag('og:url', canonicalUrl);
      updatePropertyMetaTag('og:title', seo.title);
      updatePropertyMetaTag('og:description', seo.description);
      updateMetaTag('twitter:url', canonicalUrl);
      updateMetaTag('twitter:title', seo.title);
      updateMetaTag('twitter:description', seo.description);
      return;
    }

    // Evita indexacion accidental de rutas privadas o auxiliares.
    updateMetaTag('robots', 'noindex, nofollow');
    updateCanonicalTag(`${SITE_URL}/`);
  }, [location.pathname]);

  return null;
}

// Componente para desplazar al inicio en cada cambio de ruta
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Tema personalizado para la aplicación veterinaria
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32', // Verde veterinario
      light: '#4CAF50',
      dark: '#1B5E20',
    },
    secondary: {
      main: '#F57C00', // Naranja cálido
      light: '#FF9800',
      dark: '#E65100',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <SeoManager />
          <div className="App">
            <Routes>
              {/* Rutas públicas */}
              <Route path="/" element={<DashboardInicio />} />
              <Route path="/servicios" element={<Servicios />} />
              <Route path="/nosotros" element={<Nosotros />} />
              <Route path="/petshop" element={<PetshopPublico />} />
              <Route path="/contactos" element={<Contactos />} />
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              
              {/* Rutas privadas/admin - Protegidas */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <DashboardAdmin />
                </ProtectedRoute>
              } />
              <Route path="/admin/agenda" element={
                <ProtectedRoute>
                  <AgendaPrivada />
                </ProtectedRoute>
              } />
              <Route path="/admin/pacientes" element={
                <ProtectedRoute>
                  <GestionPacientes />
                </ProtectedRoute>
              } />
              <Route path="/admin/certificados" element={
                <ProtectedRoute>
                  <CertificadosViaje />
                </ProtectedRoute>
              } />
              <Route path="/admin/inventario" element={
                <ProtectedRoute>
                  <Inventario />
                </ProtectedRoute>
              } />
              <Route path="/admin/petshop" element={
                <ProtectedRoute>
                  <Petshop />
                </ProtectedRoute>
              } />
              <Route path="/admin/finanzas" element={
                <ProtectedRoute>
                  <Finanzas />
                </ProtectedRoute>
              } />
              
              {/* Ruta catch-all para manejar rutas no encontradas */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
